import { Processor } from '.';
import { Node } from '../Node';
import { LineRenderer } from '../component';
import { Color, Transform, TransformPosition, TransformRotation } from '../../interfaces';
import { ComponentInnerState } from './Processor';

interface LineRendererInnerState extends ComponentInnerState {
	points?: Array<TransformPosition>;
	visibility?: number;
	color?: Color | null;
	colors?: Array<Color> | null;
	isDashed?: boolean;
	dashSize?: number;
	gapSize?: number;
	dashNb?: number;
}

export class LineRendererProcessor extends Processor {
	public start(component: LineRenderer, node: Node): boolean {
		const iState: LineRendererInnerState = new Object();
		const { dashSize, gapSize, dashNb, points, color } = component.state;
		const isDashed = component.state.isDashed as boolean;
		iState.points = points.map((p) => this.clone(p) as TransformPosition);
		if (Array.isArray(color)) {
			iState.colors = color.map((c) => this.clone(c) as Color);
		} else {
			iState.color = this.clone(color) as Color;
		}
		const config = {
			isDashed,
			dashSize,
			gapSize,
			dashNb,
		};

		iState.meshAssembly = this.engine.meshFactory.lineBuilder.createLine(
			component.state.points,
			component.state.color,
			config
		);
		iState.meshAssembly.transformNode.name = 'linerenderer';
		node.addChildAssembly(iState.meshAssembly);
		Object.assign(iState, config);
		this.setState(component, iState);
		return true;
	}

	public enable(component: LineRenderer): boolean {
		const iState = this.getState<LineRendererInnerState>(component);
		if (!iState.meshAssembly || !iState.meshAssembly.isReady() || component.isDisabled) {
			return false;
		}

		iState.visibility = component.state.visibility;

		const transform = component.state.transform as Transform;
		iState.meshAssembly.visibility = iState.visibility as number;
		iState.meshAssembly.localPosition = transform.position as TransformPosition;
		iState.meshAssembly.localPosition = transform.rotation as TransformRotation;
		iState.meshAssembly.shouldRender = true;
		return true;
	}

	public disable(component: LineRenderer): boolean {
		const iState = this.getState<LineRendererInnerState>(component);
		if (iState.meshAssembly) {
			iState.meshAssembly.shouldRender = false;
			return true;
		}

		return false;
	}

	public onDestroy(component: LineRenderer): boolean {
		const iState = this.getState<LineRendererInnerState>(component);
		if (iState.meshAssembly) {
			iState.meshAssembly.shouldRender = false;
			iState.meshAssembly.destroy();
			this.deleteState(component);
			return true;
		}

		return false;
	}

	public updateState(component: LineRenderer, node: Node): boolean {
		const iState = this.getState<LineRendererInnerState>(component);
		// Not possible to update since these props can be set only on Babylob mesh creation.
		if (
			!iState.meshAssembly ||
			component.state.isDashed !== iState.isDashed ||
			component.state.dashSize !== iState.dashSize ||
			component.state.gapSize !== iState.gapSize ||
			component.state.dashNb !== iState.dashNb
		) {
			return false;
		}

		const { points, color, isDashed } = component.state;
		// Update color
		let newColor: Color[] | null = null;
		if (Array.isArray(color)) {
			if (
				!iState.colors ||
				color.length !== iState.colors.length ||
				!color.every((c, i) => {
					const color = iState.color;
					if (color) {
						return this.isEqual(c, color[i]);
					}
					return false;
				})
			) {
				newColor = color;
				// update iState
				iState.colors = color.map((c) => this.clone(c) as Color);
				iState.color = null;
			}
		} else {
			if (!iState.color || !this.isEqual(color, iState.color)) {
				newColor = [color];
				// update iState
				iState.colors = null;
				iState.color = this.clone(color) as Color;
			}
		}

		// Update points
		let newPoints: TransformPosition[] | null = null;
		if (iState.points && points.length !== iState.points.length) {
			return false;
		}
		if (
			!points.every((p, i) => {
				const points = iState.points;
				if (points) {
					return this.isEqual(p, points[i]);
				}

				return false;
			})
		) {
			newPoints = points;
			// update iState
			iState.points = points.map((p) => this.clone(p) as TransformPosition);
		}
		if (newColor || newPoints) {
			// update Babylon line
			const oPoints = isDashed
				? (newPoints as TransformPosition[])
				: (newPoints as TransformPosition[]) || points;
			this.engine.meshFactory.lineBuilder.modifyLine(
				iState.meshAssembly,
				oPoints,
				newColor as Color[],
				iState.isDashed
			);
		}

		const transform = component.state.transform as Transform;
		iState.meshAssembly.localPosition = transform.position as TransformPosition;
		iState.meshAssembly.localRotation = transform.rotation as TransformRotation;

		node.addChildAssembly(iState.meshAssembly);

		return true;
	}

	private clone(a: TransformPosition | Color): TransformPosition | Color {
		return Object.assign(new Object(), a);
	}

	private isEqual(a: TransformPosition | Color, b: TransformPosition | Color): boolean {
		return Object.keys(a).every((k) => b[k] == a[k]);
	}
}
